import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Music2 } from "lucide-react";
import { api, assetUrl, errMsg } from "../../lib/api";
import { useCredits } from "../../context/CreditsContext";
import { useSolo } from "../../hooks/useVideoStudio";

const IDEAS = ["Calm piano", "Upbeat pop", "Epic cinematic", "Lo-fi chill", "Indian sitar"];

const MusicForm = ({ video, cost, onChange, onCancel }) => {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useCredits();
  const make = async () => {
    if (prompt.trim().length < 3) return toast.error("Describe the music in a few words");
    setBusy(true);
    try { onChange(await api.makeMusic(video.id, prompt.trim())); refresh(); onCancel?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2.5">
      <input value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={300} placeholder="e.g. Warm acoustic guitar, hopeful" data-testid="music-prompt"
        className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
      <div className="flex flex-wrap gap-1.5">
        {IDEAS.map((t) => <button key={t} onClick={() => setPrompt(t)} data-testid={`music-idea-${t.split(" ")[0].toLowerCase()}`} className="h-6 px-2.5 rounded-full bg-slate-100 text-[11px] text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">{t}</button>)}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={make} disabled={busy} data-testid="music-make-btn" className="h-8 px-4 rounded-full bg-emerald-600 text-white text-xs font-medium flex items-center gap-1.5 hover:bg-emerald-700 transition-colors disabled:opacity-60">
          {busy && <Loader2 size={12} className="animate-spin" />} Make music · {cost} credit{cost === 1 ? "" : "s"}
        </button>
        {onCancel && <button onClick={onCancel} data-testid="music-cancel-btn" className="text-xs text-slate-500 hover:text-slate-800">Cancel</button>}
      </div>
    </div>
  );
};

const Ready = ({ music, onSave, onReplace }) => {
  const [vol, setVol] = useState(music.volume ?? 0.8);
  const ref = useRef(null);
  const claim = useSolo("music", () => ref.current?.pause());
  useEffect(() => { setVol(music.volume ?? 0.8); }, [music.volume]);
  return (
    <div className="space-y-2.5">
      <p className="text-xs text-slate-500 truncate" data-testid="music-prompt-label">“{music.prompt}”</p>
      <audio ref={ref} src={assetUrl(music.url)} controls onPlay={claim} className="w-full h-9" data-testid="music-audio" />
      <label className="flex items-center gap-2 text-xs text-slate-500">Volume
        <input type="range" min={0} max={1} step={0.05} value={vol} onChange={(e) => setVol(Number(e.target.value))} onMouseUp={() => onSave({ music_volume: vol })}
          onTouchEnd={() => onSave({ music_volume: vol })} onKeyUp={() => onSave({ music_volume: vol })} data-testid="music-volume" className="flex-1 accent-emerald-600" />
        <span className="w-8 font-mono text-slate-700">{Math.round(vol * 100)}%</span>
      </label>
      <div className="flex gap-3 text-xs">
        <button onClick={onReplace} data-testid="music-replace-btn" className="text-emerald-700 hover:underline">Make different music</button>
        <button onClick={() => onSave({ remove_music: true })} data-testid="music-remove-btn" className="text-slate-500 hover:text-rose-600">Remove</button>
      </div>
    </div>
  );
};

export const MusicCard = ({ video, pricing, onSave, onChange }) => {
  const [replacing, setReplacing] = useState(false);
  const m = video.music;
  const cost = pricing?.music_credits ?? 1;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="music-card">
      <h3 className="text-sm font-medium text-slate-900 flex items-center gap-2"><Music2 size={15} className="text-emerald-600" /> Background music</h3>
      {m?.status === "generating" && <p className="text-xs text-slate-500 flex items-center gap-2" data-testid="music-generating"><Loader2 size={13} className="animate-spin text-emerald-600" /> Making your music — about a minute…</p>}
      {m?.status === "failed" && <p className="text-xs text-rose-600" data-testid="music-error">{m.error}</p>}
      {m?.status === "ready" && !replacing && <Ready music={m} onSave={onSave} onReplace={() => setReplacing(true)} />}
      {(!m || m.status === "failed" || replacing) && <MusicForm video={video} cost={cost} onChange={onChange} onCancel={replacing ? () => setReplacing(false) : null} />}
    </section>
  );
};

export default MusicCard;
